import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { MobileNumberField } from "../../../components/common/form/MobileNumberField";
import UpperCaseTextField from "../../../modules/common/shared/components/UpperCaseTextField";
import { useAcademicYearContext } from "../../../context/AacademicYearContext";
import { useNotify } from "../../../context/NotificationContext";
import { FacultyFieldErrors, useFacultyForm, validateDetails } from "../hooks/useFacultyForm";
import AssignmentTree from "./AssignmentTree";
import { contentCardSx } from "../../../components/common/surfaceStyles";

const FieldError: React.FC<{ text?: string }> = ({ text }) =>
  text ? (
    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
      {text}
    </Typography>
  ) : null;

/**
 * Add Faculty (Staff → Add, /app/staff/new?type=faculty) and Edit Faculty (/app/staff/faculty/:id) — one form.
 * Staff details plus the classes/subjects the faculty teaches in the active academic year.
 * Assignments are optional; on edit, unticking removes the assignment.
 */
export default function FacultyForm() {
  const { id } = useParams();
  const facultyId = id ? Number(id) : undefined;
  const isEdit = facultyId !== undefined;
  const navigate = useNavigate();
  const notify = useNotify();
  const { academicYears } = useAcademicYearContext();
  const activeYear = useMemo(
    () => (academicYears ?? []).find((year) => year.status === "ACTIVE")?.name ?? null,
    [academicYears]
  );

  const { tree, details, setDetails, ticked, toggleAll, loginId, loading, saving, loadError, reset, save } =
    useFacultyForm(facultyId);
  const [errors, setErrors] = useState<FacultyFieldErrors>({});
  // Bumped after a create so the detail fields remount clean (e.g. Phone forgets it was touched
  // and doesn't show "required" on the now-empty form).
  const [formKey, setFormKey] = useState(0);

  const setField = (field: keyof typeof details, value: string) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const fieldErrors = validateDetails(details);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    const failure = await save();
    if (failure) {
      notify(failure, "error");
      return;
    }
    if (isEdit) {
      notify("Faculty updated successfully.", "success");
    } else {
      notify("Faculty created successfully.", "success");
      reset(); // ready for the next one
      setErrors({});
      setFormKey((key) => key + 1);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ ml: { xs: "5px", md: "100px" }, mt: 2, maxWidth: 800 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button onClick={() => navigate("/app/staff?type=faculty")}>Back to directory</Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{ ...contentCardSx, display: "flex", flexDirection: "column", gap: 2, ml: { xs: 0, md: "100px" }, mt: 2, maxWidth: 800 }}
    >
      <Box>
        <Typography variant="h5" fontWeight={600}>
          {isEdit ? "Edit Faculty" : "Add Faculty"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? `Login: ${loginId ?? "—"}` : "A login is created for the faculty when saved."}
        </Typography>
      </Box>

      {/* Staff details */}
      <Box key={formKey} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
        <Box>
          <UpperCaseTextField
            value={details.name}
            setValue={(value) => setField("name", value)}
            label="Name"
            required={true}
          />
          <FieldError text={errors.name} />
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel id="faculty-gender">Gender</InputLabel>
          <Select
            labelId="faculty-gender"
            label="Gender"
            value={details.gender}
            onChange={(event) => setField("gender", event.target.value)}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <MobileNumberField
            label="Phone"
            value={details.phone || null}
            onChange={(phone) => setField("phone", phone ?? "")}
            required={true}
            fullWidth
          />
          <FieldError text={errors.phone} />
        </Box>
        <TextField
          size="small"
          fullWidth
          label="Email"
          value={details.email}
          onChange={(event) => setField("email", event.target.value)}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
      </Box>

      {/* Teaching assignments */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Teaching assignments{activeYear ? ` — ${activeYear}` : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Optional. Tick the subjects this faculty teaches; a grade or section box ticks everything under it.
        </Typography>
        {activeYear ? (
          <AssignmentTree tree={tree} ticked={ticked} onToggle={toggleAll} disabled={saving} />
        ) : (
          <Alert severity="info">
            There is no active academic year, so assignments can't be set. The faculty can still be saved.
          </Alert>
        )}
      </Box>

      <Box display="flex" gap={1}>
        <Button variant="contained" type="submit" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : isEdit ? "Save" : "Add"}
        </Button>
        {isEdit && (
          <Button onClick={() => navigate("/app/staff?type=faculty")} disabled={saving}>
            Back
          </Button>
        )}
      </Box>
    </Box>
  );
}
